import React, { useState, useEffect, useCallback } from 'react';
import Itinerary from './Itinerary';
import BudgetOptions from './BudgetOptions';
import { PDFViewer } from '@react-pdf/renderer';
import BudgetDocument from './BudgetDocument';
import { useAuth } from './AuthContext';
import { API_URL } from './authService';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

function Budgets({ initialData, onClearInitialData, onGenerateBooking }) {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    numberPassenger: '',
    quoteDisclaimer: 'Tarifas expresadas en dólares con impuestos incluídos. Tarifa por persona.\nSujeto a disponibilidad y modificación sin previo aviso.\nTasas hoteleras no incluidas.\nElección de asientos aleatorios sin cargo.\nPresupuesto válido por 72 hs.',
    commissionPercentage: 10,
    comments: '',
    passenger: { id: null },
    request: { id: null }
  });

  const [itinerary, setItinerary] = useState([]);
  const [budgetOptions, setBudgetOptions] = useState([]);
  // --- State from Requests.js for passenger selection ---
  const [passengerData, setPassengerData] = useState({ name: '', surname: '', dni: '', phoneNumber: '', email: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [passengerStatus, setPassengerStatus] = useState(''); // 'existing', 'new', 'conflict', ''
  const [conflictPassenger, setConflictPassenger] = useState(null);
  const [currentSearchField, setCurrentSearchField] = useState('');
  // ---------------------------------------------------------

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');
  const pageSize = 10;

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingBudget, setViewingBudget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isPdfDataLoading, setIsPdfDataLoading] = useState(false);

  const [searchField, setSearchField] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const { token } = useAuth();

  const fetchBudgets = useCallback(async (page = 0, sort = 'id,desc', search = '') => {
    if (!token) return;
    setLoading(true);
    try {
      const sortParam = `&sort=${sort}`;
      
      let searchParam = search ? `&${search}` : '';

      if (search) {
        if (search.includes('passenger.name.contains') || search.includes('passenger.surname.contains')) {
          const isName = search.includes('passenger.name.contains');
          const value = search.split('=')[1];
          const passengerField = isName ? 'name.contains' : 'surname.contains';
          
          const passResponse = await fetch(`${API_URL}/api/passengers?${passengerField}=${value}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (passResponse.ok) {
            const passengers = await passResponse.json();
            const passengerIds = passengers.map(p => p.id);
            
            if (passengerIds.length > 0) {
              searchParam = `&passengerId.in=${passengerIds.join(',')}`;
            } else {
              setBudgets([]); setTotalElements(0); setTotalPages(0); setLoading(false); return;
            }
          }
        } else if (search.includes('budgetDestinations.destination.destinationName.contains') || search.includes('budgetDestinations.destination.country.contains')) {
           const isDest = search.includes('budgetDestinations.destination.destinationName.contains');
           const value = search.split('=')[1];
           const destField = isDest ? 'destinationName.contains' : 'country.contains';
           
           const destResponse = await fetch(`${API_URL}/api/destinations?${destField}=${value}`, {
             headers: { 'Authorization': `Bearer ${token}` }
           });
           
           if (destResponse.ok) {
             const destinations = await destResponse.json();
             const destIds = destinations.map(d => d.id);
             
             if (destIds.length > 0) {
               const bdResponse = await fetch(`${API_URL}/api/budget-destinations?destinationId.in=${destIds.join(',')}`, {
                 headers: { 'Authorization': `Bearer ${token}` }
               });
               
               if (bdResponse.ok) {
                 const bDestinations = await bdResponse.json();
                 const budgetIds = [...new Set(bDestinations.map(bd => bd.budget?.id || bd.budgetId).filter(id => id))];
                 
                 if (budgetIds.length > 0) {
                   searchParam = `&id.in=${budgetIds.join(',')}`;
                 } else {
                   setBudgets([]); setTotalElements(0); setTotalPages(0); setLoading(false); return;
                 }
               }
             } else {
               setBudgets([]); setTotalElements(0); setTotalPages(0); setLoading(false); return;
             }
           }
        }
      }

      const response = await fetch(`${API_URL}/api/budgets?page=${page}&size=${pageSize}${sortParam}${searchParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch budgets');
      
      let data = await response.json();
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');

      data = await Promise.all(
        data.map(async (budget) => {
          let passengerDetails = null;
          if (budget.passenger && budget.passenger.id) {
            try {
              const passengerResponse = await fetch(`${API_URL}/api/passengers/${budget.passenger.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (passengerResponse.ok) passengerDetails = await passengerResponse.json();
            } catch (error) { console.error(`Error fetching passenger ${budget.passenger.id}:`, error); }
          }
          
          let budgetDestinations = [];
          if (budget.id) {
            try {
              const destinationsResponse = await fetch(`${API_URL}/api/budget-destinations?budgetId.equals=${budget.id}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              if (destinationsResponse.ok) {
                  const bDestinations = await destinationsResponse.json();
                  budgetDestinations = await Promise.all(bDestinations.map(async bd => {
                      if (bd.destination && bd.destination.id) {
                          try {
                              const destDetailsResponse = await fetch(`${API_URL}/api/destinations/${bd.destination.id}`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (destDetailsResponse.ok) {
                                  const destinationDetails = await destDetailsResponse.json();
                                  return { ...bd, destination: destinationDetails };
                              }
                          } catch (e) {
                              console.error("Failed to fetch destination details", e);
                              return { ...bd, destination: { ...bd.destination, destinationName: 'Error al cargar' } };
                          }
                      }
                      return { ...bd, destination: { ...bd.destination, destinationName: 'N/A' } };
                  }));
              }
            } catch (error) { console.error('Error fetching budget destinations:', error); }
          }

          let loadedBudgetOptions = [];
          if (budget.id) {
            try {
              const optionsRes = await fetch(`${API_URL}/api/budget-options?budgetId.equals=${budget.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
              if (optionsRes.ok) {
                const optionsData = await optionsRes.json();
                loadedBudgetOptions = await Promise.all(optionsData.map(async (opt) => {
                  let accommodations = [];
                  try {
                    const accRes = await fetch(`${API_URL}/api/budget-option-acommodations?budgetOptionId.equals=${opt.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
                    if (accRes.ok) {
                      const budgetOptionAccommodations = await accRes.json();
                      accommodations = await Promise.all(budgetOptionAccommodations.map(async (boa) => {
                        if (boa.acommodation && boa.acommodation.id) {
                          try {
                            const fullAccRes = await fetch(`${API_URL}/api/acommodations/${boa.acommodation.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
                            if (fullAccRes.ok) {
                              const fullAccData = await fullAccRes.json();
                              return { ...boa, acommodation: fullAccData };
                            }
                          } catch (e) {
                            console.error(`Failed to fetch full accommodation ${boa.acommodation.id}`, e);
                          }
                        }
                        return boa;
                      }));
                    }
                  } catch (e) { console.error(e); }
                  return { ...opt, accommodations };
                }));
              }
            } catch (e) { console.error('Error fetching budget options:', e); }
          }

          return { ...budget, passenger: passengerDetails, budgetDestinations, budgetOptions: loadedBudgetOptions };
        })
      );
      
      setBudgets(data);
      setTotalElements(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));
      setCurrentPage(page);

    } catch (error) {
      console.error('Error fetching budgets:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBudgets(currentPage, `${sortField},${sortDirection}`);
  }, [fetchBudgets, sortField, sortDirection]);

  // --- Effect to handle initial data from Requests ---
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        request: { id: initialData.requestId },
        passenger: { id: initialData.passenger?.id || null }
      }));

      if (initialData.passenger) {
        setPassengerData({
          name: initialData.passenger.name || '',
          surname: initialData.passenger.surname || '',
          dni: String(initialData.passenger.dni || ''),
          phoneNumber: initialData.passenger.phoneNumber || '',
          email: initialData.passenger.email || ''
        });
        setSelectedPassenger(initialData.passenger);
        setPassengerStatus('existing');
      }

      if (initialData.itinerary) {
        setItinerary(initialData.itinerary);
      }

      setShowForm(true);
      if (onClearInitialData) onClearInitialData();
    }
  }, [initialData, onClearInitialData]);

  // --- Functions from Requests.js for passenger handling ---
  const searchPassengers = async (field, value) => {
    if (!value || value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const fieldMap = { 'name': 'name.contains', 'surname': 'surname.contains', 'dni': 'dni.equals', 'phoneNumber': 'phoneNumber.contains', 'email': 'email.contains' };
      const searchParam = `${fieldMap[field]}=${encodeURIComponent(value)}`;
      const response = await fetch(`${API_URL}/api/passengers?${searchParam}&size=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      }
    } catch (error) { console.error('Error searching passengers:', error); }
  };

  const checkExistingPassenger = async () => {
    const { name, surname, dni } = passengerData;
    if (!name?.trim() || !surname?.trim() || !String(dni).trim()) {
      setPassengerStatus('');
      setSelectedPassenger(null);
      setConflictPassenger(null);
      return;
    }
    try {
      const dniResponse = await fetch(`${API_URL}/api/passengers?dni.equals=${encodeURIComponent(String(dni))}&size=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (dniResponse.ok) {
        const dniData = await dniResponse.json();
        if (dniData.length > 0) {
          const existing = dniData[0];
          if (existing.name?.toLowerCase().trim() === name.toLowerCase().trim() && existing.surname?.toLowerCase().trim() === surname.toLowerCase().trim()) {
            setSelectedPassenger(existing);
            setPassengerStatus('existing');
            setConflictPassenger(null);
            setFormData(prev => ({ ...prev, passenger: { id: existing.id } }));
          } else {
            setConflictPassenger(existing);
            setPassengerStatus('conflict');
            setFormData(prev => ({ ...prev, passenger: { id: null } }));
          }
          return;
        }
      }
      setSelectedPassenger(null);
      setConflictPassenger(null);
      setPassengerStatus('new');
      setFormData(prev => ({ ...prev, passenger: { id: null } }));
    } catch (error) { console.error('Error checking passenger:', error); }
  };

  const createPassengerAPI = async (pData) => {
    const response = await fetch(`${API_URL}/api/passengers`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...pData, sex: null }) // Assuming sex is not captured in this form
    });
    if (!response.ok) throw new Error("Failed to create passenger");
    return response.json();
  };

  const updatePassengerAPI = async (id, pData) => {
    const response = await fetch(`${API_URL}/api/passengers/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...pData, id })
    });
    if (!response.ok) throw new Error("Failed to update passenger");
    return response.json();
  };

  const handlePassengerInputChange = (e) => {
    const { name, value } = e.target;
    setPassengerData(prev => ({ ...prev, [name]: value }));
    setCurrentSearchField(name);
    if (['name', 'surname', 'dni'].includes(name)) {
      setPassengerStatus('');
      setSelectedPassenger(null);
      setConflictPassenger(null);
      setFormData(prev => ({ ...prev, passenger: { id: null } }));
    }
    if (value.length >= 2) searchPassengers(name, value);
    else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePassengerInputBlur = () => setTimeout(() => setShowSuggestions(false), 200);

  const handlePassengerInputFocus = (fieldName) => {
    setCurrentSearchField(fieldName);
    if (passengerData[fieldName]?.length >= 2 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const selectSuggestion = (passenger) => {
    setPassengerData({ name: passenger.name || '', surname: passenger.surname || '', dni: String(passenger.dni || ''), phoneNumber: passenger.phoneNumber || '', email: passenger.email || '' });
    setSelectedPassenger(passenger);
    setPassengerStatus('existing');
    setFormData(prev => ({ ...prev, passenger: { id: passenger.id } }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => checkExistingPassenger(), 500);
    return () => clearTimeout(timeoutId);
  }, [passengerData.name, passengerData.surname, passengerData.dni]);
  
  // ----------------------------------------------------------------

  const handlePageChange = (newPage) => {
    const sortParam = `${sortField},${sortDirection}`;
    const searchParam = buildSearchParam();
    fetchBudgets(newPage, sortParam, searchParam);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    const searchParam = buildSearchParam();
    fetchBudgets(0, `${field},${newDirection}`, searchParam);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const buildSearchParam = () => {
    if (!searchField || !searchValue.trim()) return '';
    
    const fieldMap = {
      'id': 'id.equals',
      'passengerName': 'passenger.name.contains',
      'passengerSurname': 'passenger.surname.contains',
      'destination': 'budgetDestinations.destination.destinationName.contains',
      'country': 'budgetDestinations.destination.country.contains'
    };
    
    const apiField = fieldMap[searchField];
    return apiField ? `${apiField}=${encodeURIComponent(searchValue.trim())}` : '';
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setSearchValue('');
    setCurrentPage(0);
    if (!e.target.value) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchBudgets(0, sortParam);
    }
  };

  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (searchField && searchValue.trim()) {
      const timeoutId = setTimeout(() => {
        setLoading(true);
        const sortParam = sortField ? `${sortField},${sortDirection}` : '';
        const searchParam = buildSearchParam();
        fetchBudgets(0, sortParam, searchParam);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else if (searchField && !searchValue.trim()) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchBudgets(0, sortParam);
    }
  }, [searchValue, searchField, sortField, sortDirection]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const createBudget = async (budgetData) => {
    const response = await fetch(`${API_URL}/api/budgets`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(budgetData)
    });
    if (!response.ok) throw new Error("Failed to create budget");
    return response.json();
  };
  
  const updateBudget = async (id, budgetData) => {
    const response = await fetch(`${API_URL}/api/budgets/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...budgetData, id })
    });
    if (!response.ok) throw new Error("Failed to update budget");
    return response.json();
  };

  const saveBudgetDestinations = async (budgetId, currentDestinations) => {
    
    // 1. Obtener destinos existentes en BD
    const response = await fetch(`${API_URL}/api/budget-destinations?budgetId.equals=${budgetId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    let existingDestinations = [];
    if (response.ok) existingDestinations = await response.json();

    const processedIds = [];

    // 2. Actualizar o Crear
    for (const dest of currentDestinations) {
        const payload = {
            departureDate: dest.departureDate || null,
            returnDate: dest.returnDate || null,
            budget: { id: budgetId },
            destination: { id: dest.destinationId }
        };

        // Si tiene ID y existe en la BD, actualizamos (PUT)
        if (dest.id && existingDestinations.some(e => String(e.id) === String(dest.id))) {
            await fetch(`${API_URL}/api/budget-destinations/${dest.id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...payload, id: dest.id })
            });
            processedIds.push(dest.id);
        } else {
            // Si no tiene ID (o es temporal), creamos (POST)
            await fetch(`${API_URL}/api/budget-destinations`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        }
    }

    // 3. Eliminar los que ya no están en la lista
    for (const existing of existingDestinations) {
        if (!processedIds.includes(existing.id)) {
            await fetch(`${API_URL}/api/budget-destinations/${existing.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
        }
    }
  };

  const deleteBudgetDestinations = async (budgetId) => {
      const response = await fetch(`${API_URL}/api/budget-destinations?budgetId.equals=${budgetId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
          const destinations = await response.json();
          for (const dest of destinations) {
              await fetch(`${API_URL}/api/budget-destinations/${dest.id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` }
              });
          }
      }
  };

  const saveBudgetOptions = async (budgetId, currentOptions) => {
    
    // 1. Obtener opciones existentes
    const response = await fetch(`${API_URL}/api/budget-options?budgetId.equals=${budgetId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    let existingOptions = [];
    if (response.ok) existingOptions = await response.json();

    const processedOptionIds = [];

    for (const opt of currentOptions) {
        const { id, accommodations, ...rest } = opt;
        const optPayload = { ...rest, budget: { id: budgetId } };
        
        let savedOptionId;

        if (id && existingOptions.some(e => e.id === id)) {
            // Actualizar Opción
            await fetch(`${API_URL}/api/budget-options/${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...optPayload, id })
            });
            savedOptionId = id;
            processedOptionIds.push(id);
        } else {
            // Crear Opción
            const res = await fetch(`${API_URL}/api/budget-options`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(optPayload)
            });
            if (res.ok) {
                const data = await res.json();
                savedOptionId = data.id;
            }
        }

        if (savedOptionId) {
            await saveBudgetOptionAccommodations(savedOptionId, accommodations || []);
        }
    }

    // Eliminar opciones removidas
    for (const existing of existingOptions) {
        if (!processedOptionIds.includes(existing.id)) {
            await deleteBudgetOptionAccommodations(existing.id);
            await fetch(`${API_URL}/api/budget-options/${existing.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
        }
    }
  };

  const saveBudgetOptionAccommodations = async (optionId, currentAccs) => {
      const response = await fetch(`${API_URL}/api/budget-option-acommodations?budgetOptionId.equals=${optionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
      });
      let existingAccs = [];
      if (response.ok) existingAccs = await response.json();

      const processedAccIds = [];

      for (const acc of currentAccs) {
          const { id, ...rest } = acc;
          // Accommodations now have a nested 'acommodation' object and a 'searchTerm' for UI.
          // We need to clean the payload before sending it to the API.
          const { acommodation, searchTerm, ...apiRest } = rest;
          const payload = { 
            ...apiRest, 
            budgetOption: { id: optionId },
            acommodation: acommodation ? { id: acommodation.id } : null
          };

          if (id && existingAccs.some(e => e.id === id)) {
              await fetch(`${API_URL}/api/budget-option-acommodations/${id}`, {
                  method: 'PUT',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ ...payload, id })
              });
              processedAccIds.push(id);
          } else {
              await fetch(`${API_URL}/api/budget-option-acommodations`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload)
              });
          }
      }

      for (const existing of existingAccs) {
          if (!processedAccIds.includes(existing.id)) {
              await fetch(`${API_URL}/api/budget-option-acommodations/${existing.id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` }
              });
          }
      }
  };

  const deleteBudgetOptionAccommodations = async (optionId) => {
      const response = await fetch(`${API_URL}/api/budget-option-acommodations?budgetOptionId.equals=${optionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
          const accs = await response.json();
          for (const acc of accs) {
              await fetch(`${API_URL}/api/budget-option-acommodations/${acc.id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` }
              });
          }
      }
  };

  const deleteBudgetOptions = async (budgetId) => {
    // We assume we need to fetch options first to delete them, or use the loaded data if available.
    // But to be safe, let's fetch IDs by budgetId.
    // Since we don't have a direct "delete all by budgetId" endpoint usually, we iterate.
    // However, for simplicity in this context, we rely on the fact that we are replacing them.
    // A robust implementation would fetch existing options for the budget and delete them one by one,
    // and for each option, delete its accommodations first.
    // Given the complexity, we will rely on the `budgetToDelete` logic or `editingId` logic.
    
    try {
        const optionsRes = await fetch(`${API_URL}/api/budget-options?budgetId.equals=${budgetId}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (optionsRes.ok) {
            const options = await optionsRes.json();
            for (const opt of options) {
                // Delete accommodations for this option
                const accRes = await fetch(`${API_URL}/api/budget-option-acommodations?budgetOptionId.equals=${opt.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
                if (accRes.ok) {
                    const accs = await accRes.json();
                    for (const acc of accs) {
                        await fetch(`${API_URL}/api/budget-option-acommodations/${acc.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                    }
                }
                // Delete the option
                await fetch(`${API_URL}/api/budget-options/${opt.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            }
        }
    } catch (e) { console.error('Error deleting budget options:', e); }
  };

  const deleteBudgetAPI = async (id) => {
    const response = await fetch(`${API_URL}/api/budgets/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error("Failed to delete budget");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passengerStatus === 'conflict') {
      alert('No se puede guardar el presupuesto. Existe un conflicto con el DNI del pasajero.');
      return;
    }
    try {
      let finalPassengerId = formData.passenger.id;
      if (finalPassengerId && selectedPassenger && passengerStatus === 'existing') {
        const infoChanged = (passengerData.phoneNumber || '') !== (selectedPassenger.phoneNumber || '') || (passengerData.email || '') !== (selectedPassenger.email || '');
        if (infoChanged) {
          await updatePassengerAPI(finalPassengerId, { ...selectedPassenger, phoneNumber: passengerData.phoneNumber, email: passengerData.email });
        }
      }
      if (!finalPassengerId && passengerStatus === 'new') {
        const newPassenger = await createPassengerAPI(passengerData);
        finalPassengerId = newPassenger.id;
      }
      
      const budgetPayload = {
        ...formData,
        passenger: { id: finalPassengerId },
        // Si no hay un ID de solicitud, enviamos null en lugar de { id: null }
        request: formData.request?.id ? { id: formData.request.id } : null
      };

      if (editingId) {
        await updateBudget(editingId, budgetPayload);
        await saveBudgetDestinations(editingId, itinerary);
        await saveBudgetOptions(editingId, budgetOptions);
      } else {
        const newBudget = await createBudget(budgetPayload);
        await saveBudgetDestinations(newBudget.id, itinerary);
        await saveBudgetOptions(newBudget.id, budgetOptions);
      }
      resetForm();
      const sortParam = `${sortField},${sortDirection}`;
      const searchParam = buildSearchParam();
      fetchBudgets(0, sortParam, searchParam);
    } catch (error) {
      console.error('Error saving budget:', error);
      alert('Error al guardar el presupuesto.');
    }
  };

  const fetchPassengerById = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/passengers/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const p = await response.json();
        setPassengerData({ name: p.name || '', surname: p.surname || '', dni: String(p.dni || ''), phoneNumber: p.phoneNumber || '', email: p.email || '' });
        setSelectedPassenger(p);
        setPassengerStatus('existing');
      }
    } catch (error) { console.error('Error fetching passenger by ID:', error); }
  };

  const handleEdit = (budget) => {
    setFormData({
        numberPassenger: budget.numberPassenger || '',
        quoteDisclaimer: budget.quoteDisclaimer || '',
        commissionPercentage: budget.commissionPercentage ?? 10,
        comments: budget.comments || '',
        passenger: { id: budget.passenger?.id || null },
        request: { id: budget.request?.id || null }
    });
    if (budget.passenger && budget.passenger.id) {
      fetchPassengerById(budget.passenger.id);
    }
    
    if (budget.budgetDestinations && Array.isArray(budget.budgetDestinations)) {
        const adaptedItinerary = budget.budgetDestinations.map(bd => {
            const start = new Date(bd.departureDate);
            const end = new Date(bd.returnDate);
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            return {
                id: bd.id,
                destinationId: bd.destination ? bd.destination.id : null,
                destinationName: bd.destination ? `${bd.destination.destinationName} - ${bd.destination.country}` : "Desconocido",
                departureDate: bd.departureDate,
                returnDate: bd.returnDate,
                numberNights: diffDays || 0
            };
        });
        setItinerary(adaptedItinerary);
    } else {
        setItinerary([]);
    }

    // When loading for edit, ensure the 'searchTerm' is initialized for the accommodation search UI
    const loadedOptions = (budget.budgetOptions || []).map(opt => ({
        ...opt,
        accommodations: (opt.accommodations || []).map(acc => ({
            ...acc,
            searchTerm: acc.acommodation ? acc.acommodation.acommodationName : ''
        }))
    }));
    setBudgetOptions(loadedOptions);

    setEditingId(budget.id);
    setShowForm(true);
  };
  
  const handleView = async (budget) => {
    setIsPdfDataLoading(true);
    setShowViewModal(true);
    // Set budget without images first to show modal content quickly
    setViewingBudget(budget);

    try {
        // Get unique destinations to avoid fetching the same image multiple times
        const uniqueDestinations = budget.budgetDestinations
            ? Array.from(new Map(budget.budgetDestinations.map(bd => [bd.destination.id, bd.destination])).values())
            : [];

        // Fetch image for each unique destination
        const destinationsWithImagesPromises = uniqueDestinations.map(async (dest) => {
            if (!dest || !dest.id) return { ...dest, imageUrl: null };
            try {
                const response = await fetch(`${API_URL}/api/destinations/${dest.id}/image`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok && response.headers.get('Content-Type')?.startsWith('image')) {
                    const imageBlob = await response.blob();
                    // Convert blob to data URL for @react-pdf/renderer
                    const imageUrl = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(imageBlob);
                    });
                    return { ...dest, imageUrl };
                }
                return { ...dest, imageUrl: null };
            } catch (error) {
                console.error(`Error fetching image for destination ${dest.id}:`, error);
                return { ...dest, imageUrl: null };
            }
        });

        const destinationsWithImages = await Promise.all(destinationsWithImagesPromises);
        
        // Fetch images for each accommodation in each option
        const optionsWithImagesPromises = (budget.budgetOptions || []).map(async (opt) => {
            const accommodationsWithImagesPromises = (opt.accommodations || []).map(async (acc) => {
                if (!acc.acommodation || !acc.acommodation.id) return acc;

                const fetchImage = async (position) => {
                    try {
                        const response = await fetch(`${API_URL}/api/acommodations/${acc.acommodation.id}/image/${position}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok && response.headers.get('Content-Type')?.startsWith('image')) {
                            const imageBlob = await response.blob();
                            return new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result);
                                reader.onerror = reject;
                                reader.readAsDataURL(imageBlob);
                            });
                        }
                        return null;
                    } catch (error) {
                        console.error(`Error fetching image ${position} for accommodation ${acc.acommodation.id}:`, error);
                        return null;
                    }
                };

                const [imageUrl1, imageUrl2] = await Promise.all([fetchImage(1), fetchImage(2)]);

                return {
                    ...acc,
                    acommodation: { ...acc.acommodation, imageUrl1, imageUrl2 }
                };
            });

            const accommodationsWithImages = await Promise.all(accommodationsWithImagesPromises);
            return { ...opt, accommodations: accommodationsWithImages };
        });

        const optionsWithImages = await Promise.all(optionsWithImagesPromises);

        const imageUrlMap = new Map(destinationsWithImages.map(d => [d.id, d.imageUrl]));

        // Create an updated budget object with image URLs embedded in destinations
        const updatedBudget = {
            ...budget,
            budgetDestinations: budget.budgetDestinations.map(bd => ({
                ...bd,
                destination: {
                    ...bd.destination,
                    imageUrl: imageUrlMap.get(bd.destination.id) || null
                }
            })),
            budgetOptions: optionsWithImages
        };

        setViewingBudget(updatedBudget);

    } catch (error) {
        console.error("Error preparing budget data for view:", error);
    } finally {
        setIsPdfDataLoading(false);
    }
  };

  const handleDelete = (id) => {
    setBudgetToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setShowPdfPreview(false);
    setViewingBudget(null);
  };

  const handleGenerateBookingClick = (budget) => {
    if (onGenerateBooking) {
      const adaptedItinerary = budget.budgetDestinations ? budget.budgetDestinations.map((bd, index) => ({
        id: Date.now() + index, // ID temporal para la UI
        destinationId: bd.destination ? bd.destination.id : null,
        destinationName: bd.destination ? `${bd.destination.destinationName} - ${bd.destination.country}` : "Desconocido",
        departureDate: bd.departureDate,
        returnDate: bd.returnDate,
        numberNights: (bd.departureDate && bd.returnDate) ? 
          Math.ceil(Math.abs(new Date(bd.returnDate) - new Date(bd.departureDate)) / (1000 * 60 * 60 * 24)) : 0
      })) : [];

      onGenerateBooking({
        budgetId: budget.id,
        passenger: budget.passenger,
        itinerary: adaptedItinerary,
        numberPassengers: budget.numberPassenger
      });
    }
  };

  const confirmDelete = async () => {
    if (budgetToDelete) {
        try {
            await deleteBudgetDestinations(budgetToDelete);
            await deleteBudgetOptions(budgetToDelete);
            await deleteBudgetAPI(budgetToDelete);
            setShowDeleteModal(false);
            setBudgetToDelete(null);
            const sortParam = `${sortField},${sortDirection}`;
            const searchParam = buildSearchParam();
            fetchBudgets(currentPage, sortParam, searchParam);
        } catch (error) { console.error('Error deleting budget:', error); }
    }
  };

  const resetForm = () => {
    setFormData({ numberPassenger: '', quoteDisclaimer: 'Tarifas expresadas en dólares con impuestos incluídos. Tarifa por persona.\nSujeto a disponibilidad y modificación sin previo aviso.\nTasas hoteleras no incluidas.\nElección de asientos aleatorios sin cargo.\nPresupuesto válido por 72 hs.', commissionPercentage: 10, comments: '', passenger: { id: null }, request: { id: null } });
    setPassengerData({ name: '', surname: '', dni: '', phoneNumber: '', email: '' });
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedPassenger(null);
    setConflictPassenger(null);
    setPassengerStatus('');
    setItinerary([]);
    setBudgetOptions([]);
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Gestión de Presupuestos</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} style={styles.addButton}>
          + Agregar Presupuesto
        </button>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchField}>
          <label style={styles.searchLabel}>Buscar por:</label>
          <select 
            value={searchField} 
            onChange={handleSearchFieldChange} 
            style={styles.searchSelect}
          >
            <option value="">Seleccionar campo</option>
            <option value="id">ID</option>
            <option value="passengerName">Nombre del Pasajero</option>
            <option value="passengerSurname">Apellido del Pasajero</option>
            <option value="destination">Destino</option>
            <option value="country">País de Destino</option>
          </select>
        </div>
        {searchField && (
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>Valor:</label>
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchValueChange}
              placeholder={`Buscar por ${searchField === 'id' ? 'ID' : searchField === 'passengerName' ? 'nombre' : searchField === 'passengerSurname' ? 'apellido' : searchField === 'destination' ? 'destino' : 'país'}...`}
              style={styles.searchInput}
            />
          </div>
        )}
        {(searchField || searchValue) && (
          <button 
            onClick={() => {
              setSearchField('');
              setSearchValue('');
              setCurrentPage(0);
              setLoading(true);
              const sortParam = sortField ? `${sortField},${sortDirection}` : '';
              fetchBudgets(0, sortParam);
            }}
            style={styles.clearButton}
          >
            Limpiar
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.formTitle}>{editingId ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}</h3>
          {formData.request?.id && (
            <div style={styles.requestBadge}>
              📋 Generando presupuesto basado en la Solicitud #{formData.request.id}
            </div>
          )}
          
          <div style={styles.passengerSection}>
            <h4 style={styles.sectionTitle}>Datos del Pasajero</h4>
            {passengerStatus === 'existing' && <div style={styles.statusMessage}>✅ Pasajero existente: {selectedPassenger?.name} {selectedPassenger?.surname}</div>}
            {passengerStatus === 'new' && <div style={styles.statusMessageNew}>➕ Se creará un nuevo pasajero con los datos ingresados.</div>}
            {passengerStatus === 'conflict' && <div style={styles.statusMessageError}>⚠️ CONFLICTO: El DNI ya pertenece a {conflictPassenger?.name} {conflictPassenger?.surname}.</div>}
            
            <div style={styles.passengerGrid}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Apellido *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="surname" value={passengerData.surname} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('surname')} style={styles.input} required autoComplete="off" />
                   {showSuggestions && suggestions.length > 0 && currentSearchField === 'surname' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Nombre *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="name" value={passengerData.name} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('name')} style={styles.input} required autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'name' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>DNI *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="dni" value={passengerData.dni} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('dni')} style={styles.input} required autoComplete="off" />
                   {showSuggestions && suggestions.length > 0 && currentSearchField === 'dni' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Teléfono</label>
                <div style={styles.autocompleteContainer}>
                  <input name="phoneNumber" value={passengerData.phoneNumber} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('phoneNumber')} style={styles.input} autoComplete="off" />
                   {showSuggestions && suggestions.length > 0 && currentSearchField === 'phoneNumber' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - Tel: {p.phoneNumber}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Email</label>
                <div style={styles.autocompleteContainer}>
                  <input name="email" type="email" value={passengerData.email} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('email')} style={styles.input} autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'email' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - Email: {p.email}</div>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Itinerary itinerary={itinerary} setItinerary={setItinerary} />

          <BudgetOptions options={budgetOptions} setOptions={setBudgetOptions} commissionPercentage={formData.commissionPercentage} itinerary={itinerary} />

          <div style={styles.formGrid}>
            <div style={{...styles.fieldContainer, ...styles.internalSection}}>
              <label style={styles.label}>Porcentaje de Comisión (%) <span style={styles.internalBadge}>Uso Interno</span></label>
              <input name="commissionPercentage" type="number" value={formData.commissionPercentage} onChange={handleInputChange} style={{...styles.input, backgroundColor: '#f8f9fa'}} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Número de Pasajeros</label>
              <input name="numberPassenger" type="number" value={formData.numberPassenger} onChange={handleInputChange} style={styles.input} required />
            </div>
            <div style={{...styles.fieldContainer, ...styles.internalSection}}>
              <label style={styles.label}>Comentarios <span style={styles.internalBadge}>Uso Interno</span></label>
              <textarea name="comments" value={formData.comments} onChange={handleInputChange} style={{...styles.textarea, backgroundColor: '#f8f9fa'}} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Disclaimer de Cotización <span style={styles.publicBadge}>Visible en PDF</span></label>
              <textarea name="quoteDisclaimer" value={formData.quoteDisclaimer} onChange={handleInputChange} style={styles.textarea} />
            </div>
          </div>
          <div style={styles.formButtons}>
            <button type="button" onClick={resetForm} style={styles.cancelButton}>Cancelar</button>
            <button type="submit" style={styles.saveButton}>Guardar</button>
          </div>
        </form>
      )}

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>Cargando presupuestos...</div>
        ) : budgets.length === 0 ? (
          <div style={styles.emptyState}>No hay presupuestos registrados.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.sortableHeader} onClick={() => handleSort('id')}>ID{getSortArrow('id')}</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('passenger.name')}>Pasajero{getSortArrow('passenger.name')}</th>
                  <th style={styles.tableHeader}>Destinos</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('numberPassenger')}>Nº Pasajeros{getSortArrow('numberPassenger')}</th>
                  <th style={styles.tableHeader}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {budgets.map(budget => (
                  <tr key={budget.id}>
                    <td style={styles.tableCell}>{budget.id}</td>
                    <td style={styles.tableCell}>{budget.passenger ? `${budget.passenger.surname} ${budget.passenger.name}` : 'N/A'}</td>
                    <td style={styles.tableCell}>
                        {budget.budgetDestinations?.map(bd => bd.destination ? `${bd.destination.destinationName} - ${bd.destination.country}` : null).filter(Boolean).join(', ') || 'N/A'}
                    </td>
                    <td style={styles.tableCell}>{budget.numberPassenger}</td>
                    <td style={styles.tableCell}>
                      <div style={styles.actionButtons}>
                        <button onClick={() => handleGenerateBookingClick(budget)} style={styles.generateButton}>Reservar</button>
                        <button onClick={() => handleView(budget)} style={styles.viewButton}>Ver</button>
                        <button onClick={() => handleEdit(budget)} style={styles.editButton}>Editar</button>
                        <button onClick={() => handleDelete(budget.id)} style={styles.deleteButton}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} de {totalElements} presupuestos
              </div>
              <div style={styles.paginationControls}>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} style={currentPage === 0 ? styles.paginationButtonDisabled : styles.paginationButton}>Anterior</button>
                <span style={styles.pageInfo}>Página {currentPage + 1} de {totalPages}</span>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} style={currentPage >= totalPages - 1 ? styles.paginationButtonDisabled : styles.paginationButton}>Siguiente</button>
              </div>
            </div>
          </>
        )}
      </div>

      {showViewModal && viewingBudget && (
        <div style={styles.modalOverlay}>
          <div style={showPdfPreview ? styles.pdfViewModal : styles.viewModal}>
            {showPdfPreview ? (
              <>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>{`Presupuesto #${viewingBudget.id} - ${viewingBudget.passenger?.surname || 'Cliente'}`}</h3>
                  <button onClick={() => setShowPdfPreview(false)} style={styles.modalCancelButton} type="button">Volver a Detalles</button>
                </div>
                <PDFViewer style={{ width: '100%', height: 'calc(90vh - 80px)' }}>
                  <BudgetDocument budget={viewingBudget} />
                </PDFViewer>
              </>
            ) : (
              <>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>Información del Presupuesto</h3>
                  <button onClick={handleCloseViewModal} style={styles.closeButton} type="button">×</button>
                </div>
                <div style={styles.viewContent}>
                  {viewingBudget.passenger && (
                    <div style={styles.passengerInfoSection}>
                      <h4 style={styles.passengerInfoTitle}>Datos del Pasajero</h4>
                      <div style={styles.viewGrid}>
                        <div style={styles.viewRow}><strong>Apellido:</strong> {viewingBudget.passenger.surname || 'N/A'}</div>
                        <div style={styles.viewRow}><strong>Nombre:</strong> {viewingBudget.passenger.name || 'N/A'}</div>
                        <div style={styles.viewRow}><strong>DNI:</strong> {viewingBudget.passenger.dni || 'N/A'}</div>
                        <div style={styles.viewRow}><strong>Teléfono:</strong> {viewingBudget.passenger.phoneNumber || 'N/A'}</div>
                        <div style={styles.viewRow}><strong>Email:</strong> {viewingBudget.passenger.email || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                  
                  <div style={styles.itinerarySection}>
                      <h4 style={styles.sectionTitle}>Itinerario</h4>
                      {viewingBudget.budgetDestinations && viewingBudget.budgetDestinations.length > 0 ? (
                          <table style={{...styles.table, ...styles.viewTable}}>
                              <thead>
                                  <tr>
                                      <th style={styles.viewTableHeader}>Destino</th>
                                      <th style={styles.viewTableHeader}>Salida</th>
                                      <th style={styles.viewTableHeader}>Regreso</th>
                                      <th style={styles.viewTableHeader}>Noches</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {viewingBudget.budgetDestinations.map(bd => {
                                      const nights = (bd.departureDate && bd.returnDate) ? 
                                          Math.ceil(Math.abs(new Date(bd.returnDate) - new Date(bd.departureDate)) / (1000 * 60 * 60 * 24)) : 0;
                                      return (
                                          <tr key={bd.id}>
                                              <td style={styles.viewTableCell}>{bd.destination ? `${bd.destination.destinationName} - ${bd.destination.country}` : 'N/A'}</td>
                                              <td style={styles.viewTableCell}>{formatDate(bd.departureDate)}</td>
                                              <td style={styles.viewTableCell}>{formatDate(bd.returnDate)}</td>
                                              <td style={styles.viewTableCell}>{nights}</td>
                                          </tr>
                                      );
                                  })}
                              </tbody>
                          </table>
                      ) : <p>No hay destinos en el itinerario.</p>}
                  </div>

                  {viewingBudget.budgetOptions && viewingBudget.budgetOptions.length > 0 && (
                    <div style={styles.optionsSection}>
                      <h4 style={styles.sectionTitle}>Opciones de Presupuesto</h4>
                      {viewingBudget.budgetOptions.map((opt, idx) => (
                        <div key={opt.id} style={styles.viewOptionCard}>
                          <h5 style={styles.viewOptionTitle}>Opción {idx + 1} - {opt.typeTrip} ({opt.travelCompany})</h5>
                          <div style={{...styles.viewGrid, gridTemplateColumns: 'repeat(4, 1fr)'}}>
                            <div style={styles.viewRow}><strong>Subtotal:</strong> ${opt.optionPrice?.toFixed(2) || '0.00'}</div>
                            <div style={styles.viewRow}><strong>Comisión:</strong> {viewingBudget.commissionPercentage || 0}%</div>
                            <div style={styles.viewRow}><strong>Precio Final:</strong> ${opt.finalPrice?.toFixed(2) || '0.00'}</div>
                            <div style={styles.viewRow}><strong>Precio p/ Persona:</strong> ${((opt.finalPrice || 0) / (viewingBudget.numberPassenger || 1)).toFixed(2)}</div>
                          </div>
                          <div style={styles.viewGrid}>
                            <div style={styles.viewRow}><strong>Origen:</strong> {opt.origin}</div>
                            <div style={styles.viewRow}><strong>Equipaje:</strong> {opt.luggage}</div>
                            <div style={styles.viewRow}><strong>Precio Tickets:</strong> ${opt.ticketsPrice || '0'}</div>
                            <div style={styles.viewRow}><strong>Transfer:</strong> {opt.transfer ? `Sí (${opt.transferDescription || ''}) - $${opt.transferPrice || 0}` : 'No'}</div>
                            <div style={styles.viewRow}><strong>Auto:</strong> {opt.carRental ? `Sí (${opt.carRentalDescription || ''}) - $${opt.carRentalPrice || 0}` : 'No'}</div>
                            <div style={styles.viewRow}><strong>Excursión:</strong> {opt.excursion ? `Sí (${opt.excursionDescription || ''}) - $${opt.excursionPrice || 0}` : 'No'}</div>
                            <div style={styles.viewRow}><strong>Seguro:</strong> {opt.travelInsurance ? `Sí (${opt.travelInsuranceDescription || ''}) - $${opt.travelInsurancePrice || 0}` : 'No'}</div>
                            <div style={styles.viewRow}><strong>Impuestos:</strong> {opt.taxes ? 'Sí' : 'No'}</div>
                            <div style={styles.viewRow}><strong>Com. Transfer:</strong> {opt.transferComments || 'N/A'}</div>
                            <div style={styles.viewRow}><strong>Com. Auto:</strong> {opt.carRentalComments || 'N/A'}</div>
                            <div style={styles.viewRow}><strong>Com. Excursión:</strong> {opt.excursionComments || 'N/A'}</div>
                            <div style={styles.viewRow}><strong>Com. Seguro:</strong> {opt.travelInsuranceComments || 'N/A'}</div>
                            <div style={styles.viewRow}><strong>Com. Impuestos:</strong> {opt.taxesComments || 'N/A'}</div>
                          </div>
                          {opt.accommodations && opt.accommodations.length > 0 && (
                            <div style={{marginTop: '10px', paddingLeft: '10px', borderLeft: '3px solid #eee'}}>
                              <strong>Alojamientos:</strong>
                              <ul style={{margin: '5px 0', paddingLeft: '20px'}}>
                                {opt.accommodations.map(acc => (
                                  <li key={acc.id}>{acc.acommodation?.acommodationName || 'Alojamiento no especificado'} - {acc.accomodationDescription} (${acc.accomodationPrice || '0'}) - {acc.acommodationComments || 'Sin comentarios'}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={styles.budgetInfoSection}>
                    <h4 style={styles.budgetInfoTitle}>Datos del Presupuesto</h4>
                    <div style={styles.viewGrid}>
                      <div style={styles.viewRow}><strong>ID Presupuesto:</strong> {viewingBudget.id}</div>
                      <div style={styles.viewRow}><strong>ID Solicitud:</strong> {viewingBudget.request?.id || 'N/A'}</div>
                      <div style={styles.viewRow}><strong>Comisión:</strong> {viewingBudget.commissionPercentage}%</div>
                      <div style={styles.viewRow}><strong>Número de Pasajeros:</strong> {viewingBudget.numberPassenger}</div>
                      <div style={styles.viewRow}><strong>Comentarios:</strong> {viewingBudget.comments}</div>
                      <div style={styles.viewRow}><strong>Disclaimer:</strong> {viewingBudget.quoteDisclaimer}</div>
                    </div>
                  </div>
                </div>
                <div style={styles.modalButtons}>
                  <button 
                    onClick={() => setShowPdfPreview(true)} 
                    style={styles.pdfButton}
                    disabled={isPdfDataLoading}
                  >
                    {isPdfDataLoading ? 'Cargando Datos...' : 'Previsualizar PDF'}
                  </button>
                  <button onClick={handleCloseViewModal} style={styles.modalCancelButton}>Cerrar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirmar Eliminación</h3>
            <p style={styles.modalText}>¿Estás seguro de que deseas eliminar este presupuesto?</p>
            <div style={styles.modalButtons}>
              <button onClick={() => setShowDeleteModal(false)} style={styles.modalCancelButton}>Cancelar</button>
              <button onClick={confirmDelete} style={styles.modalConfirmButton}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
    container: { padding: '5px', maxWidth: '100%', margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
    title: { fontSize: '36px', fontWeight: 'bold', color: '#333', margin: 0 },
    addButton: { backgroundColor: '#ff9f43', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
    form: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', marginBottom: '30px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
    formTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '20px', color: '#333' },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '25px' },
    fieldContainer: { display: 'flex', flexDirection: 'column' },
    internalSection: { backgroundColor: '#fdfdfd', padding: '10px', borderRadius: '8px' },
    internalBadge: { fontSize: '11px', color: '#6c757d', backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px', fontWeight: 'normal' },
    publicBadge: { fontSize: '11px', color: '#28a745', backgroundColor: '#d4edda', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px', fontWeight: 'normal' },
    label: { fontSize: '16px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' },
    input: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
    select: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff', appearance: 'none' },
    textarea: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff', minHeight: '80px', resize: 'vertical' },
    formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
    saveButton: { backgroundColor: '#28a745', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
    cancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
    tableContainer: { backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
    table: { width: '100%', borderCollapse: 'collapse' },
    tableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontSize: '18px' },
    sortableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', cursor: 'pointer', userSelect: 'none' },
    tableCell: { padding: '20px 40px', borderBottom: '1px solid #dee2e6', fontSize: '18px' },
    actionButtons: { display: 'flex', gap: '8px', whiteSpace: 'nowrap' },
    viewButton: { backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
    editButton: { backgroundColor: '#17a2b8', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
    deleteButton: { backgroundColor: '#dc3545', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
    generateButton: { backgroundColor: '#6f42c1', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
    emptyState: { textAlign: 'center', padding: '40px', color: '#6c757d', fontSize: '20px' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' },
    paginationInfo: { fontSize: '18px', color: '#6c757d' },
    paginationControls: { display: 'flex', alignItems: 'center', gap: '15px' },
    paginationButton: { padding: '12px 24px', backgroundColor: '#ff9f43', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '18px' },
    paginationButtonDisabled: { padding: '12px 24px', backgroundColor: '#e9ecef', color: '#6c757d', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontSize: '18px' },
    pageInfo: { fontSize: '18px', fontWeight: '500', color: '#495057' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '400px', width: '90%' },
    viewModal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '800px', width: '90%', maxHeight: '80vh', overflowY: 'auto' },
    modalTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '15px', color: '#333', textAlign: 'center' },
    pdfViewModal: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column' },
    modalText: { fontSize: '20px', color: '#495057', marginBottom: '25px', textAlign: 'center', lineHeight: '1.5' },
    modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
    modalCancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
    modalConfirmButton: { backgroundColor: '#dc3545', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
    pdfButton: { backgroundColor: '#17a2b8', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
    viewContent: { marginBottom: '25px' },
  viewRow: { padding: '12px 0', borderBottom: '1px solid #e9ecef', fontSize: '20px' },
    passengerSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' },
    sectionTitle: { fontSize: '20px', fontWeight: '600', color: '#495057', marginBottom: '15px', borderBottom: '2px solid #ff9f43', paddingBottom: '5px' },
    passengerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' },
    autocompleteContainer: { position: 'relative' },
    suggestionsDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
    suggestionItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1', fontSize: '16px' },
    statusMessage: { backgroundColor: '#d4edda', color: '#155724', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #c3e6cb', fontSize: '16px' },
    statusMessageNew: { backgroundColor: '#fff3cd', color: '#856404', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #ffeaa7', fontSize: '16px' },
    statusMessageError: { backgroundColor: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #f5c6cb', fontSize: '16px' },
    requestBadge: { backgroundColor: '#e2e3e5', color: '#383d41', padding: '12px', borderRadius: '6px', marginBottom: '20px', border: '1px solid #d6d8db', fontSize: '16px', fontWeight: 'bold', textAlign: 'center' },
    itinerarySection: { marginBottom: '30px', padding: '20px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #e0e0e0' },
  viewTable: { fontSize: '18px' },
    viewTableHeader: { backgroundColor: '#e9ecef', padding: '10px', textAlign: 'left' },
    viewTableCell: { padding: '10px', borderBottom: '1px solid #f1f1f1' },
    searchContainer: { backgroundColor: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' },
    searchField: { display: 'flex', flexDirection: 'column', minWidth: '200px' },
    searchLabel: { fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '5px' },
    searchSelect: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff' },
    searchInput: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff', minWidth: '250px' },
    clearButton: { backgroundColor: '#6c757d', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', height: 'fit-content' },
    passengerInfoSection: { marginBottom: '25px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' },
  passengerInfoTitle: { fontSize: '22px', fontWeight: '600', color: '#495057', marginBottom: '10px', borderBottom: '2px solid #ff9f43', paddingBottom: '5px' },
    viewGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    closeButton: { backgroundColor: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6c757d', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' },
    budgetInfoSection: { marginBottom: '15px' },
  budgetInfoTitle: { fontSize: '22px', fontWeight: '600', color: '#495057', marginBottom: '10px', borderBottom: '2px solid #17a2b8', paddingBottom: '5px' },
    optionsSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeeba' },
    viewOptionCard: { backgroundColor: 'white', padding: '15px', borderRadius: '8px', marginBottom: '10px', border: '1px solid #dee2e6' },
  viewOptionTitle: { margin: '0 0 10px 0', fontSize: '20px', color: '#856404', fontWeight: 'bold' },
};

export default Budgets;